// Batch 27 · Add-on 1 — one helper for "show exactly which fields are missing".
// A refusal from the server carries `missing_fields: [{field, label_en, label_ar, reason_en, reason_ar}]` (its old message
// stays in `detail`). `apply(err)` marks those fields: red border (the destructive token), the reason under the field
// (EN / AR), the first one scrolled to and focused, a toast with the count. A field clears as soon as it is filled
// (`filled(field, value)` from its onChange) or when the next save succeeds (`reset()`).
import React, { useCallback, useState } from "react";
import { toast } from "sonner";

export const MF_TEXT = {
  en: { one: "1 field needs your attention — it is marked in red.", many: "{n} fields need your attention — they are marked in red.",
        required: "Required" },
  ar: { one: "حقل واحد يحتاج إلى انتباهك — وُضعت عليه علامة باللون الأحمر.", many: "{n} حقول تحتاج إلى انتباهك — وُضعت عليها علامة باللون الأحمر.",
        required: "مطلوب" },
};

export const MISSING_CLASS = "border-destructive ring-1 ring-destructive focus-visible:ring-destructive";

export function missingFromError(err) {
  const mf = err?.response?.data?.missing_fields ?? err?.missing_fields;
  return Array.isArray(mf) ? mf.filter((m) => m && m.field) : [];
}

const esc = (s) => String(s).replace(/["\\]/g, "\\$&");

export function focusField(field, root) {
  const base = root || (typeof document !== "undefined" ? document : null);
  const el = base && base.querySelector(`[data-field="${esc(field)}"]`);
  if (!el) return false;
  if (el.scrollIntoView) el.scrollIntoView({ block: "center", behavior: "smooth" });
  const target = el.matches && el.matches("input,textarea,select,button,[tabindex]") ? el
    : el.querySelector && el.querySelector("input,textarea,select,button,[tabindex]");
  if (target && target.focus) target.focus();
  return true;
}

const isFilled = (v) => !(v === undefined || v === null || (typeof v === "string" && v.trim() === "") || (Array.isArray(v) && !v.length));

export default function useMissingFields(lang = "en", { root } = {}) {
  const [missing, setMissing] = useState({});
  const L = MF_TEXT[lang === "ar" ? "ar" : "en"];

  const apply = useCallback((err) => {
    const list = missingFromError(err);
    if (!list.length) return false;
    const map = {};
    for (const m of list) if (!map[m.field]) map[m.field] = m;
    setMissing(map);
    const n = Object.keys(map).length;
    toast.error(n === 1 ? L.one : L.many.replace("{n}", String(n)));
    const first = list[0].field;
    setTimeout(() => focusField(first, root && root.current), 0);
    return true;
  }, [L, root]);

  const clear = useCallback((field) => setMissing((m) => {
    if (!m[field]) return m;
    const n = { ...m }; delete n[field]; return n;
  }), []);
  const filled = useCallback((field, value) => { if (isFilled(value)) clear(field); }, [clear]);
  const reset = useCallback(() => setMissing({}), []);
  const has = (field) => !!missing[field];
  const message = (field) => (missing[field] ? (lang === "ar" ? missing[field].reason_ar : missing[field].reason_en) || "" : "");
  // spread on the input (or its wrapper): the key to find it, the red border, aria-invalid
  const props = (field, className = "") => ({
    "data-field": field,
    "aria-invalid": missing[field] ? true : undefined,
    className: `${className} ${missing[field] ? MISSING_CLASS : ""}`.trim(),
  });
  return { missing, apply, clear, filled, reset, has, message, props, count: Object.keys(missing).length };
}

// The reason under the field, in the screen's language
export function FieldMessage({ mf, field, testid }) {
  const msg = mf && mf.message(field);
  if (!msg) return null;
  return <div role="alert" className="mt-1 text-[11px] text-destructive" data-testid={testid || `mf-msg-${field}`}>{msg}</div>;
}

// The "required" marker shown before any refusal (e.g. 105(a) "Reason for revision" once a saved figure changes)
export function RequiredMark({ lang = "en", show = true }) {
  if (!show) return null;
  return <span className="ms-1 text-destructive" aria-label={MF_TEXT[lang === "ar" ? "ar" : "en"].required} data-testid="mf-required">*</span>;
}
